import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Dropdown, Menu } from "antd";
import {
  ArrowDropDownRounded,
  BoltOutlined,
  BusinessOutlined,
  CloseRounded,
  CreditCardOutlined,
  Face5Outlined,
  GridViewOutlined,
  GroupAddOutlined,
  HomeMiniOutlined,
  LightbulbCircleOutlined,
  LogoutOutlined,
  MenuRounded,
  PriceCheckOutlined,
  SettingsOutlined,
  CalendarMonthOutlined,
  DeleteOutlineOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import TopBanner from "../utils/TopBanner";
import SolutionsMegaMenu from "./SolutionsMegaMenu";

// Mount dropdown popups inside the fixed <nav> instead of document.body so
// they share the navbar's coordinate space: menus stay glued to their trigger
// while the page scrolls or the bar animates, and an open menu can no longer
// grow the document's scrollable area (which could flash a scrollbar and
// shift the whole layout under the pointer).
const getNavPopupContainer = (triggerNode) =>
  triggerNode.closest("nav") || document.body;

// Size menus to their content, not to their container. When the scrolled
// navbar is a shrink-wrapped pill, an absolutely positioned menu near its
// right edge gets squeezed (text wraps, width changes), which makes antd
// re-align it, which un-squeezes it, and so on — the menu jitters between two
// positions every frame. A fixed content width breaks that loop.
const navMenuOverlayStyle = { width: "max-content" };

// Pin the click menus to their placement. With auto-adjust on, antd flips a
// menu above its trigger or shifts it sideways whenever it measures an
// overflow, and every scroll or resize re-runs that check — a measurement that
// lands on the edge makes the menu hop up and down. A menu anchored under the
// top navbar always has room below it, so there is nothing to adjust for.
const navMenuProps = {
  trigger: ["click"],
  overlayStyle: navMenuOverlayStyle,
  autoAdjustOverflow: false,
  getPopupContainer: getNavPopupContainer,
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Track which desktop nav dropdowns are open. When the scrolled navbar is in
  // its collapsed "pill" state, hovering expands it; crossing the few pixels of
  // bare page between a trigger and its open menu fires the nav's mouseleave,
  // which would collapse the bar and yank the dropdown's anchor out from under
  // it, making the menu jump up and down. Keeping the bar expanded while any
  // dropdown is open holds the anchor still.
  const [openDropdowns, setOpenDropdowns] = useState({});
  const anyDropdownOpen = Object.values(openDropdowns).some(Boolean);
  // Whether the pointer is currently over the <nav>, so a dropdown that closes
  // after the pointer has already left the bar can collapse the pill again.
  const pointerInNav = useRef(false);
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const onDashboard = location.pathname.startsWith("/dashboard");
  const isHomePage = location.pathname === "/";
  // The home hero is dark, so the navbar should render in light text until
  // the user scrolls past the hero. On every other page the navbar stays
  // in its default dark-text style.
  const onDarkHero = isHomePage && !isScrolled;
  const hasLivestreamStudioAccess = Boolean(
    currentUser?.Plan && currentUser.Plan.toLowerCase().includes("pro")
  );

  useEffect(() => {
    // Coalesce scroll events into one update per animation frame and use a
    // passive listener so scrolling stays smooth on mobile instead of firing
    // a setState on every raw scroll event.
    let ticking = false;

    const update = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(scrolled);
      setIsExpanded(!scrolled);
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close the mobile menu whenever the route changes so it never lingers
  // over a freshly navigated page.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent the page behind the mobile menu from scrolling while it's open.
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const linkClass = `py-2 px-3 font-semibold transition-colors whitespace-nowrap ${
    onDarkHero ? "text-white hover:text-white/70" : "text-gray-800 hover:text-gray-600"
  }`;
  const dropdownTriggerClass = `py-2 px-3 flex items-center cursor-pointer font-semibold transition-colors whitespace-nowrap ${
    onDarkHero ? "text-white hover:text-white/70" : "text-gray-800 hover:text-gray-600"
  }`;
  const brandClass = `flex items-center py-5 px-2 min-w-0 ${
    onDarkHero ? "text-white hover:text-white/80" : "text-gray-800 hover:text-gray-900"
  }`;
  const ctaBtnClass = onDarkHero
    ? "inline-flex items-center justify-center h-9 px-5 whitespace-nowrap rounded-full bg-white text-black text-sm font-semibold tracking-tight transition-all duration-150 hover:bg-neutral-200"
    : "inline-flex items-center justify-center h-9 px-5 whitespace-nowrap rounded-full bg-black text-white text-sm font-semibold tracking-tight transition-all duration-150 hover:bg-neutral-800";
  // Styling for the mobile slide-down menu rows.
  const mobileMenuItemClass =
    "flex items-center gap-3 w-full rounded-xl px-3 py-2.5 text-[15px] font-medium text-gray-800 hover:bg-gray-100 active:bg-gray-200 transition-colors";
  const mobileMenuSectionLabel =
    "px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400";

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Keep the collapsed navbar open while a dropdown is open so its trigger
  // (and therefore the dropdown's alignment target) never moves.
  const handleDropdownOpenChange = (key) => (open) => {
    const next = { ...openDropdowns, [key]: open };
    setOpenDropdowns(next);
    if (!isScrolled) return;
    if (open) {
      setIsExpanded(true);
    } else if (!pointerInNav.current && !Object.values(next).some(Boolean)) {
      // The last open menu closed after the pointer had already left the bar
      // (mouseleave was ignored while it was open), so collapse the pill now.
      setIsExpanded(false);
    }
  };

  const dashboardLinks = [
    {
      label: "Affiliate",
      to: "/affiliate",
      icon: <GroupAddOutlined fontSize="small" />,
    },
    {
      label: "Feature Request",
      to: "https://zenstream.canny.io/feature-requests",
      icon: <LightbulbCircleOutlined fontSize="small" />,
      external: true,
    },
  ];

  const twitchMenuIcon = (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
    </svg>
  );

  // Primary navigation shown at the top of the mobile menu. The set depends on
  // whether the user is signed in.
  const mobilePrimaryLinks = currentUser
    ? [
        {
          label: "Dashboard",
          to: "/dashboard",
          icon: <HomeMiniOutlined fontSize="small" />,
        },
        {
          label: hasLivestreamStudioAccess ? "Live Studio" : "Livestreams",
          to: hasLivestreamStudioAccess ? "/livestream/studio" : "/livestream",
          icon: <BoltOutlined fontSize="small" />,
        },
        {
          label: "Scheduler",
          to: "/calendar",
          icon: <CalendarMonthOutlined fontSize="small" />,
        },
        {
          label: "Twitch Automation",
          to: "/twitch-automation",
          icon: twitchMenuIcon,
        },
        {
          label: "Solutions",
          to: "/solutions",
          icon: <GridViewOutlined fontSize="small" />,
        },
        {
          label: "Pricing",
          to: "/pricing",
          icon: <PriceCheckOutlined fontSize="small" />,
        },
      ]
    : [
        {
          label: "Solutions",
          to: "/solutions",
          icon: <GridViewOutlined fontSize="small" />,
        },
        {
          label: "Pricing",
          to: "/pricing",
          icon: <PriceCheckOutlined fontSize="small" />,
        },
        {
          label: "Enterprise",
          to: "/enterprise/sales",
          icon: <BusinessOutlined fontSize="small" />,
        },
      ];

  const mobileToolsLinks = [
    { label: "Profanity Filter", to: "/features/profanity_filter" },
    { label: "Face Blurring", to: "/features/face_blurring" },
    { label: "Image Blurring", to: "/features/image_blurring" },
    { label: "Learning Center", to: "/learning-center" },
  ];

  // Account links for signed-in users. The dashboard-only shortcuts keep their
  // original "only on /dashboard" behaviour from the desktop nav.
  const mobileAccountLinks = [
    ...(onDashboard ? dashboardLinks : []),
    {
      label: "Settings",
      to: "/settings",
      icon: <SettingsOutlined fontSize="small" />,
    },
    {
      label: "Subscriptions",
      to: "/manage-subscriptions",
      icon: <CreditCardOutlined fontSize="small" />,
    },
  ];

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const resourceItems = [
    {
      label: (
        <div className="flex items-center">
          <BoltOutlined className="mr-2" />
          <span>
            {hasLivestreamStudioAccess ? "Livestream Studio" : "Livestream AI"}
          </span>
        </div>
      ),
      key: "2a",
      onClick: () =>
        navigate(hasLivestreamStudioAccess ? "/livestream/studio" : "/livestream"),
    },
    {
      label: (
        <div className="flex items-center">
          <span>Profanity Filter</span>
        </div>
      ),
      key: "1",
      onClick: () => navigate("/features/profanity_filter"),
    },
    {
      label: (
        <div className="flex items-center">
          <span>Face Blurring</span>
        </div>
      ),
      key: "2",
      onClick: () => navigate("/features/face_blurring"),
    },
    {
      label: (
        <div className="flex items-center">
          <span>Image Blurring</span>
        </div>
      ),
      key: "3",
      onClick: () => navigate("/features/image_blurring"),
    },
    {
      label: (
        <div className="flex items-center">
          <span>Learning Center</span>
        </div>
      ),
      key: "4",
      onClick: () => navigate("/learning-center"),
    },
  ];

  const items = [
    {
      label: (
        <div className="flex items-center">
          <HomeMiniOutlined className="mr-2" />
          <span>Dashboard</span>
        </div>
      ),
      key: "1",
      onClick: () => navigate("/dashboard"),
    },
    {
      label: (
        <div className="flex items-center">
          <CalendarMonthOutlined className="mr-2" />
          <span>Scheduler</span>
        </div>
      ),
      key: "2",
      onClick: () => navigate("/calendar"),
    },
    {
      label: (
        <div className="flex items-center">
          <span className="mr-2" style={{ fontSize: 20 }}>
            <svg
              viewBox="0 0 24 24"
              width="1em"
              height="1em"
              fill="currentColor"
              style={{ verticalAlign: "middle" }}
            >
              <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
            </svg>
          </span>
          <span>Twitch Automation</span>
        </div>
      ),
      key: "2b",
      onClick: () => navigate("/twitch-automation"),
    },
    {
      label: (
        <div className="flex items-center">
          <SettingsOutlined className="mr-2" />
          <span>Settings</span>
        </div>
      ),
      key: "3",
      onClick: () => navigate("/settings"),
    },
    {
      label: (
        <div className="flex items-center">
          <CreditCardOutlined className="mr-2" />
          <span>Subscriptions</span>
        </div>
      ),
      key: "4",
      onClick: () => navigate("/manage-subscriptions"),
    },
    {
      type: "divider",
    },
    {
      label: (
        <div className="flex items-center text-red-500">
          <DeleteOutlineOutlined className="mr-2" />
          <span>Delete Account</span>
        </div>
      ),
      key: "5",
      onClick: () => navigate("/delete-account"),
    },
    {
      label: (
        <div className="flex items-center text-red-500">
          <LogoutOutlined className="mr-2" />
          <span>Logout</span>
        </div>
      ),
      key: "6",
      onClick: () => logout(),
    },
  ];

  const resourceMenu = <Menu items={resourceItems} />;
  const profileMenu = <Menu items={items} />;
  const solutionsOverlay = <SolutionsMegaMenu />;

  const liveStreamBtnStyles = `
    inline-flex items-center justify-center gap-2
    h-9
    px-4
    whitespace-nowrap
    rounded-lg
    bg-fuchsia-700
    text-white
    text-sm
    font-semibold
    tracking-tight
    shadow-[3px_3px_0px_0px_rgba(0,0,0,0.9)]
    transition-all duration-150
    hover:bg-fuchsia-800
    hover:translate-x-[1px]
    hover:translate-y-[1px]
    hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)]
    active:translate-x-[2px]
    active:translate-y-[2px]
    active:shadow-[1px_1px_0px_0px_rgba(0,0,0,0.9)]
  `;

  const studioBtnStyles = `
    relative
    inline-flex items-center justify-center gap-2
    h-9
    px-4
    whitespace-nowrap
    rounded-lg
    bg-gradient-to-r from-fuchsia-600 via-pink-500 to-rose-500
    text-white
    text-sm
    font-semibold
    tracking-tight
    shadow-[3px_3px_0px_0px_rgba(0,0,0,0.9)]
    transition-all duration-150
    hover:brightness-110
    hover:translate-x-[1px]
    hover:translate-y-[1px]
    hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)]
    active:translate-x-[2px]
    active:translate-y-[2px]
    active:shadow-[1px_1px_0px_0px_rgba(0,0,0,0.9)]
  `;



  return (
    <>
      {currentUser !== null && currentUser?.Plan === "Free" && (
        <TopBanner
          message="Choose a plan to have full access."
          cta="You're using the free version of Zenstream which limits you to 1 video a month. "
          icon={<Face5Outlined />}
          buttonText="Begin"
          navigateTo="pricing"
        />
      )}

      {/* Dim + blur the page behind the open mobile menu; tap to dismiss. */}
      <div
        className={`md:hidden fixed inset-0 z-10 bg-black/30 backdrop-blur-sm transition-opacity duration-200 ${
          mobileMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      <nav
        className={`fixed z-20 transition-all duration-300 ease-in-out backdrop-blur-lg
          ${
            isScrolled
              ? "top-4 left-4 right-4 md:right-auto rounded-full bg-white/90 border border-black/10 shadow-sm"
              : onDarkHero
              ? "w-full bg-transparent border-b border-white/10"
              : "w-full bg-white/80 border-b border-black/5"
          }`}
        onMouseEnter={() => {
          pointerInNav.current = true;
          if (isScrolled) setIsExpanded(true);
        }}
        onMouseLeave={() => {
          pointerInNav.current = false;
          if (isScrolled && !anyDropdownOpen) setIsExpanded(false);
        }}
      >
        <div
          className={`mx-auto h-16 flex justify-between px-4 transition-all duration-300
            ease-in-out ${
              isScrolled ? (isExpanded ? "w-full" : "w-full md:w-auto") : "w-full"
            }`}
        >
          <div className="flex items-center space-x-8 min-w-0">
            <a href="/" className={brandClass} aria-label="Zenstream home">
              <span className="font-bold whitespace-nowrap">Zenstream</span>
            </a>
          </div>

          <div
            className={`hidden md:flex items-center overflow-hidden transition-all duration-300
              ease-in-out ${
                isExpanded || !isScrolled
                  ? "opacity-100 max-w-full"
                  : "opacity-0 max-w-0"
              }`}
          >
            <div className="flex items-center justify-end space-x-4">
              {currentUser ? (
                <>
                  <a
                    href="/dashboard"
                    className={linkClass}
                  >
                    Dashboard
                  </a>

                  {/* Hover-only: pairing "click" with "hover" made a click on
                      the trigger toggle the already-hover-opened menu closed,
                      then pointer movement re-opened it — the menu flickered
                      open/closed under the cursor. With hover alone a click on
                      the trigger is harmless and the menu stays put. */}
                  <Dropdown
                    overlay={solutionsOverlay}
                    placement="bottom"
                    trigger={["hover"]}
                    mouseLeaveDelay={0.2}
                    getPopupContainer={getNavPopupContainer}
                    onOpenChange={handleDropdownOpenChange("solutions")}
                  >
                    <div>
                      <p className={dropdownTriggerClass}>
                        Solutions <ArrowDropDownRounded />
                      </p>
                    </div>
                  </Dropdown>

                  <a
                    href="/pricing"
                    className={linkClass}
                  >
                    Pricing
                  </a>

                  <a
                    href={hasLivestreamStudioAccess ? "/livestream/studio" : "/livestream"}
                    className={hasLivestreamStudioAccess ? studioBtnStyles : liveStreamBtnStyles}
                  >
                    {hasLivestreamStudioAccess ? (
                      <>
                        <span className="relative flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/80 opacity-75" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
                        </span>
                        Live Studio
                      </>
                    ) : (
                      "LiveStreams"
                    )}
                  </a>

                  {onDashboard &&
                    dashboardLinks.map((link) =>
                      link.external ? (
                        <a
                          key={link.label}
                          href={link.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="py-2 px-3 flex items-center gap-2 font-semibold hover:text-gray-600 transition-colors whitespace-nowrap"
                        >
                          {link.icon}
                          {link.label}
                        </a>
                      ) : (
                        <a
                          key={link.label}
                          href={link.to}
                          className="py-2 px-3 flex items-center gap-2 font-semibold hover:text-gray-600 transition-colors whitespace-nowrap"
                        >
                          {link.icon}
                          {link.label}
                        </a>
                      )
                    )}

                  <Dropdown
                    {...navMenuProps}
                    overlay={resourceMenu}
                    placement="bottom"
                    onOpenChange={handleDropdownOpenChange("tools")}
                  >
                    <div>
                      <p className={dropdownTriggerClass}>
                        Tools <ArrowDropDownRounded />
                      </p>
                    </div>
                  </Dropdown>

                  <Dropdown
                    {...navMenuProps}
                    overlay={profileMenu}
                    placement="bottomRight"
                    onOpenChange={handleDropdownOpenChange("profile")}
                  >
                    <div className="cursor-pointer flex items-center">
                      <img
                        src={
                          currentUser.ProfilePicture ||
                          "https://via.placeholder.com/40"
                        }
                        alt="Profile"
                        className="h-8 w-8 rounded-full border border-gray-200"
                      />
                    </div>
                  </Dropdown>
                </>
              ) : (
                <>
                  {/* Hover-only: pairing "click" with "hover" made a click on
                      the trigger toggle the already-hover-opened menu closed,
                      then pointer movement re-opened it — the menu flickered
                      open/closed under the cursor. With hover alone a click on
                      the trigger is harmless and the menu stays put. */}
                  <Dropdown
                    overlay={solutionsOverlay}
                    placement="bottom"
                    trigger={["hover"]}
                    mouseLeaveDelay={0.2}
                    getPopupContainer={getNavPopupContainer}
                    onOpenChange={handleDropdownOpenChange("solutions")}
                  >
                    <div>
                      <p className={dropdownTriggerClass}>
                        Solutions <ArrowDropDownRounded />
                      </p>
                    </div>
                  </Dropdown>

                  <Dropdown
                    {...navMenuProps}
                    overlay={resourceMenu}
                    placement="bottom"
                    onOpenChange={handleDropdownOpenChange("tools")}
                  >
                    <div>
                      <p className={dropdownTriggerClass}>
                        Tools <ArrowDropDownRounded />
                      </p>
                    </div>
                  </Dropdown>

                  <a
                    href="/pricing"
                    className={linkClass}
                  >
                    Pricing
                  </a>

                  <a
                    href="/enterprise/sales"
                    className={linkClass}
                  >
                    Enterprise
                  </a>

                  <a href="/login" className={ctaBtnClass}>
                    Login
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu trigger — keeps the bar to just the brand + one
              button so nothing overlaps the Zenstream wordmark. */}
          <div className="md:hidden flex items-center">
            <button
              type="button"
              onClick={() => setMobileMenuOpen((open) => !open)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav-menu"
              className={`inline-flex items-center justify-center h-10 w-10 rounded-full transition-colors ${
                onDarkHero
                  ? "text-white hover:bg-white/10"
                  : "text-gray-800 hover:bg-black/5"
              }`}
            >
              {mobileMenuOpen ? <CloseRounded /> : <MenuRounded />}
            </button>
          </div>

          {isScrolled && !isExpanded && (
            <button
              onClick={toggleExpand}
              className="hidden md:block ml-2 rounded-full p-2 hover:bg-white/70 transition-all duration-300 ease-in-out"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-gray-700"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Mobile slide-down menu — holds every nav destination in one
            scrollable, easy-to-tap sheet. */}
        <div
          id="mobile-nav-menu"
          className={`md:hidden absolute left-2 right-2 top-full mt-2 origin-top rounded-2xl border border-black/10 bg-white text-gray-900 shadow-2xl transition-all duration-200 ease-out ${
            mobileMenuOpen
              ? "pointer-events-auto translate-y-0 scale-100 opacity-100"
              : "pointer-events-none -translate-y-2 scale-[0.98] opacity-0"
          }`}
        >
          <div className="mobile-menu-scroll p-3">
            {currentUser ? (
              <>
                <div className="mb-1 flex items-center gap-3 border-b border-gray-100 px-2 pb-3">
                  <img
                    src={
                      currentUser.ProfilePicture ||
                      "https://via.placeholder.com/40"
                    }
                    alt="Profile"
                    className="h-10 w-10 rounded-full border border-gray-200"
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-gray-900">
                      {currentUser.email || "My account"}
                    </div>
                    {currentUser.Plan && (
                      <div className="text-xs text-gray-500">
                        {currentUser.Plan} plan
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-0.5">
                  {mobilePrimaryLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.to}
                      onClick={closeMobileMenu}
                      className={mobileMenuItemClass}
                    >
                      <span className="flex w-5 items-center justify-center text-gray-500">
                        {link.icon}
                      </span>
                      <span>{link.label}</span>
                    </a>
                  ))}
                </div>

                <div className="mt-1 border-t border-gray-100 pt-1">
                  <div className={mobileMenuSectionLabel}>Tools</div>
                  <div className="space-y-0.5">
                    {mobileToolsLinks.map((link) => (
                      <a
                        key={link.label}
                        href={link.to}
                        onClick={closeMobileMenu}
                        className={mobileMenuItemClass}
                      >
                        <span className="w-5" />
                        <span>{link.label}</span>
                      </a>
                    ))}
                  </div>
                </div>

                <div className="mt-1 border-t border-gray-100 pt-1">
                  <div className={mobileMenuSectionLabel}>Account</div>
                  <div className="space-y-0.5">
                    {mobileAccountLinks.map((link) =>
                      link.external ? (
                        <a
                          key={link.label}
                          href={link.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMobileMenu}
                          className={mobileMenuItemClass}
                        >
                          <span className="flex w-5 items-center justify-center text-gray-500">
                            {link.icon}
                          </span>
                          <span>{link.label}</span>
                        </a>
                      ) : (
                        <a
                          key={link.label}
                          href={link.to}
                          onClick={closeMobileMenu}
                          className={mobileMenuItemClass}
                        >
                          <span className="flex w-5 items-center justify-center text-gray-500">
                            {link.icon}
                          </span>
                          <span>{link.label}</span>
                        </a>
                      )
                    )}
                  </div>
                </div>

                <div className="mt-1 border-t border-gray-100 pt-1">
                  <a
                    href="/delete-account"
                    onClick={closeMobileMenu}
                    className={`${mobileMenuItemClass} text-red-600 hover:bg-red-50`}
                  >
                    <span className="flex w-5 items-center justify-center">
                      <DeleteOutlineOutlined fontSize="small" />
                    </span>
                    <span>Delete Account</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      closeMobileMenu();
                      logout();
                    }}
                    className={`${mobileMenuItemClass} text-red-600 hover:bg-red-50`}
                  >
                    <span className="flex w-5 items-center justify-center">
                      <LogoutOutlined fontSize="small" />
                    </span>
                    <span>Logout</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-0.5">
                  {mobilePrimaryLinks.map((link) => (
                    <a
                      key={link.label}
                      href={link.to}
                      onClick={closeMobileMenu}
                      className={mobileMenuItemClass}
                    >
                      <span className="flex w-5 items-center justify-center text-gray-500">
                        {link.icon}
                      </span>
                      <span>{link.label}</span>
                    </a>
                  ))}
                </div>

                <div className="mt-1 border-t border-gray-100 pt-1">
                  <div className={mobileMenuSectionLabel}>Tools</div>
                  <div className="space-y-0.5">
                    {mobileToolsLinks.map((link) => (
                      <a
                        key={link.label}
                        href={link.to}
                        onClick={closeMobileMenu}
                        className={mobileMenuItemClass}
                      >
                        <span className="w-5" />
                        <span>{link.label}</span>
                      </a>
                    ))}
                  </div>
                </div>

                <div className="mt-2 border-t border-gray-100 px-1 pt-3">
                  <a
                    href="/login"
                    onClick={closeMobileMenu}
                    className="flex h-11 w-full items-center justify-center rounded-xl bg-black text-sm font-semibold text-white transition-colors hover:bg-gray-900"
                  >
                    Login
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
